import { PRODUCT_MENU_GROUP_LABELS } from "@artisancode/api-types";
import { AlertTriangle, ChevronRight, Loader2, Trash2 } from "lucide-react";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { formatCurrency, formatCurrencyRounded } from "@/lib/utils";

import { ProductionCostSummary } from "./production-cost-summary";
import { ProductionIngredientTable } from "./production-ingredient-table";

import type { MenuDraft, useProductionForm } from "./use-production-form";

interface Props {
  menu: MenuDraft;
  index: number;
  form: ReturnType<typeof useProductionForm>;
}

/** One menu of a production batch: product, target, and its recipe ingredients. */
export function ProductionMenuCard({ menu, index, form }: Props) {
  const { isSubmitting } = form;
  const summary = form.menuSummary(menu);
  const canRemove = form.menus.length > 1;

  return (
    <div className="space-y-4 rounded-lg border p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">Menu {index + 1}</span>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => form.removeMenu(menu.key)}
            disabled={isSubmitting}
            className="h-8 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="mr-1 h-4 w-4" />
            Hapus
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2 sm:col-span-2">
          <Label>
            Produk yang Diproduksi <span className="text-destructive">*</span>
          </Label>
          <SearchableSelect
            value={menu.productId}
            onChange={(id) => form.setMenuProduct(menu.key, id)}
            disabled={form.isLoadingProducts || isSubmitting}
            placeholder={
              form.isLoadingProducts
                ? "Memuat katalog produk..."
                : "Cari produk minuman / makanan..."
            }
            options={form.producedProducts
              // A menu appears once per batch; the server rejects duplicates too.
              .filter(
                (p) =>
                  p.id === menu.productId || !form.takenProductIds.has(p.id),
              )
              .map((p) => ({
                value: p.id,
                label: `${p.name} (${p.sku || "Tanpa SKU"})`,
                group: PRODUCT_MENU_GROUP_LABELS[p.menuGroup],
              }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`prod-yield-${menu.key}`}>
            Target Output (Cup / Porsi)
          </Label>
          <NumberInput
            id={`prod-yield-${menu.key}`}
            value={
              menu.targetYieldStr ? Number(menu.targetYieldStr) : undefined
            }
            onChange={(n) => form.setMenuYield(menu.key, n ? String(n) : "")}
            placeholder="Misal: 20"
            className="font-semibold"
            disabled={isSubmitting}
          />
        </div>
      </div>

      {menu.isLoadingPrefill && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          Menghitung kebutuhan bahan baku dari resep...
        </div>
      )}

      {menu.prefillError && !menu.isLoadingPrefill && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-semibold">Resep Tidak Tersedia</p>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
              {menu.prefillError}
            </p>
          </div>
        </div>
      )}

      {menu.prefillMeta && !menu.isLoadingPrefill && (
        // Collapsed by default: a batch of several menus stays short; the summary
        // line keeps the cost and any shortage visible without opening it.
        <details className="group rounded-md border bg-muted/20">
          <summary className="flex cursor-pointer list-none flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between [&::-webkit-details-marker]:hidden">
            <div className="flex flex-wrap items-center gap-2">
              <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
              <span className="text-sm font-semibold">
                Kebutuhan Bahan Baku ({menu.ingredients.length} bahan)
              </span>
              <Badge variant="outline" className="text-xs">
                Resep #{menu.prefillMeta.recipeVersion}
              </Badge>
              {summary.hasShortage && (
                <Badge variant="destructive" className="text-xs">
                  Stok Bar Kurang
                </Badge>
              )}
            </div>
            <span className="pl-6 text-xs text-muted-foreground sm:pl-0">
              Total {formatCurrencyRounded(summary.totalInputCost)} · HPP{" "}
              {formatCurrency(summary.costPerUnit)}/cup
              {summary.grossMarginPercent != null &&
                ` · Margin ${summary.grossMarginPercent}%`}
            </span>
          </summary>

          <div className="space-y-4 border-t p-3">
            <ProductionIngredientTable
              ingredients={menu.ingredients}
              disabled={isSubmitting}
              onQtyChange={(idx, val) => form.changeQty(menu.key, idx, val)}
            />

            <ProductionCostSummary
              totalInputCost={summary.totalInputCost}
              costPerUnit={summary.costPerUnit}
              sellingPrice={summary.sellingPrice}
              grossMarginPercent={summary.grossMarginPercent}
            />
          </div>
        </details>
      )}
    </div>
  );
}
